// src/rms/data/demoData.js
// ============================================================================
// The demo data, and getting rid of it
// ============================================================================
// Every new operator is provisioned with a worked example so the screens have
// something to show. It is a guide, not the operator's records, and it says so
// on every row: seedOperator stamps is_demo (20260819090000_rms_demo_data_flag).
//
// That single predicate is what lets this file offer a real "delete the demo
// data" rather than the only honest alternative before it existed -- delete
// everything and start again.
//
// Two rules shape the deletion.
//
// ORDER. Children before parents, so nothing is briefly orphaned and the
// foreign keys never have to choose. The local backend has no constraints and
// would not care; Supabase does, and the two must behave the same.
//
// REFERENCES WIN. A demo vehicle the operator has since booked out for real is
// no longer only demo data -- deleting it would take the operator's booking's
// subject with it. Those rows are kept and reported, so the caller can say what
// was left behind instead of quietly leaving it.
// ============================================================================

import db from "./index";
import { createSuccessResponse, createErrorResponse } from "../utils/response";

// Children first. Within a tier order does not matter.
const DELETE_ORDER = [
  // the worked example's paperwork
  "payments",
  "invoices",
  "agreements",
  "conditionReports",
  "usageReadings",
  "reservationAddons",
  "reservationAnswers",
  "deliveries",
  "accessCredentials",
  "certifications",
  "occupancies",
  "subscriptions",
  "reservations",
  "availabilityBlocks",
  // what it was about
  "assets",
  "customers",
  // starter configuration the seed wrote on the operator's behalf
  "rateRules",
  "addons",
  "usageFeeSchedules",
  "cancellationPolicies",
  "checkoutQuestions",
  "messageTemplates",
  "assetGroups",
  "locations",
];

/** Demo rows in one collection, for this operator. */
async function demoRows(operator, collection) {
  const rows = (await db.list(collection, { operator_id: operator.id })).data || [];
  return rows.filter((r) => r.is_demo);
}

/**
 * How much demo data is left. Drives the button's label and lets it disappear
 * once there is nothing to delete.
 */
export async function countDemoRows(operator) {
  if (!operator) return 0;
  let total = 0;
  for (const collection of DELETE_ORDER) {
    total += (await demoRows(operator, collection)).length;
  }
  return total;
}

/**
 * Deletes the demo data and nothing else.
 *
 * Returns { deleted, kept }: how many demo rows went, and how many were held
 * back because the operator's own records point at them.
 */
export async function deleteDemoData(operator) {
  if (!operator) return createErrorResponse("No operator loaded.");

  // Which demo assets and customers the operator's OWN bookings depend on.
  // Computed before anything is deleted -- once the demo reservations are gone
  // the surviving references are exactly the real ones, but reading it first
  // keeps the answer true even if a delete fails half way through.
  const reservations = (await db.list("reservations", { operator_id: operator.id })).data || [];
  const realReservations = reservations.filter((r) => !r.is_demo);
  const pinnedAssets = new Set(realReservations.map((r) => r.asset_id).filter(Boolean));
  const pinnedCustomers = new Set(realReservations.map((r) => r.customer_id).filter(Boolean));

  let deleted = 0;
  let kept = 0;

  for (const collection of DELETE_ORDER) {
    const pinned =
      collection === "assets" ? pinnedAssets : collection === "customers" ? pinnedCustomers : null;

    for (const row of await demoRows(operator, collection)) {
      if (pinned && pinned.has(row.id)) {
        kept += 1;
        continue;
      }
      const result = await db.remove(collection, row.id);
      if (result.success) deleted += 1;
      else return createErrorResponse(result.error || `Could not delete from ${collection}.`);
    }
  }

  return createSuccessResponse({ deleted, kept });
}

export default deleteDemoData;
